import type { AbstractEngine } from "@babylonjs/core/Engines/abstractEngine";

import type { GameConfig } from "@/config/gameConfig";
import { Harvest } from "@/game/Harvest";
import { HarvestGauge } from "@/hud/HarvestGauge";
import { ActionButton, CHOP_BUTTON } from "@/input/ActionButton";
import { InputManager } from "@/input/InputManager";
import { VirtualJoystick } from "@/input/VirtualJoystick";
import type { SceneId } from "@/scenes/sceneId";
import { describeError, logger } from "@/utils/logger";

import type { EngineBackend } from "./createEngine";
import { createEngine } from "./createEngine";
import { SceneManager } from "./scene/SceneManager";
import type { SceneContext, SceneRegistry } from "./scene/types";

/**
 * Orchestrateur du socle : possède le moteur, la boucle de rendu, le
 * gestionnaire de scènes et les entrées, et garantit leur destruction propre.
 *
 * `Game.create()` remplace le constructeur car l'initialisation du moteur est
 * asynchrone (WebGPU) : on ne peut pas rendre un objet à moitié construit.
 */
export class Game {
  readonly engine: AbstractEngine;
  readonly backend: EngineBackend;
  readonly input: InputManager;
  /** Récolte de la partie : la scène l'alimente, la jauge du HUD l'affiche. */
  readonly harvest: Harvest;

  private readonly canvas: HTMLCanvasElement;
  private readonly config: GameConfig;
  private readonly scenes: SceneManager;
  private readonly joystick: VirtualJoystick;
  private readonly chopButton: ActionButton;
  private readonly gauge: HarvestGauge;
  private readonly resizeObserver: ResizeObserver;

  private running = false;
  private disposed = false;

  private constructor(params: {
    canvas: HTMLCanvasElement;
    config: GameConfig;
    engine: AbstractEngine;
    backend: EngineBackend;
    registry: SceneRegistry;
  }) {
    this.canvas = params.canvas;
    this.config = params.config;
    this.engine = params.engine;
    this.backend = params.backend;
    this.input = new InputManager();
    this.harvest = new Harvest();

    // Widgets à l'écran, montés à côté du canvas pour suivre son empilement et
    // sa destruction. Aucune scène ne les connaît : les deux premiers publient
    // dans l'InputManager comme le clavier, la jauge observe la récolte.
    const parent = this.canvas.parentElement ?? document.body;

    // Un seul manche, sous le pouce gauche : il conduit et il tourne.
    this.joystick = new VirtualJoystick(this.input, { parent });
    // Le pouce droit prend la coupe, seule commande qui restait au clavier :
    // c'est ce qui rend le jeu jouable de bout en bout sans clavier.
    this.chopButton = new ActionButton(this.input, { ...CHOP_BUTTON, parent });
    this.gauge = new HarvestGauge(this.harvest, { parent });

    const context: SceneContext = {
      engine: this.engine,
      canvas: this.canvas,
      input: this.input,
      harvest: this.harvest,
      goTo: (id: SceneId) => {
        void this.goTo(id);
      },
    };

    this.scenes = new SceneManager(params.registry, context);

    // ResizeObserver plutôt que l'évènement `resize` de window : il capte aussi
    // les changements de taille dus au layout (panneau d'inspecteur qui
    // s'ouvre, conteneur redimensionné) et pas seulement ceux de la fenêtre.
    this.resizeObserver = new ResizeObserver(() => {
      this.applyHardwareScaling();
      this.engine.resize();
    });
  }

  static async create(
    config: GameConfig,
    registry: SceneRegistry,
    canvas?: HTMLCanvasElement,
  ): Promise<Game> {
    const target = canvas ?? document.querySelector<HTMLCanvasElement>(config.canvasSelector);
    if (!target) {
      throw new Error(`Canvas introuvable pour le sélecteur « ${config.canvasSelector} ».`);
    }

    const { engine, backend } = await createEngine(target, {
      renderer: config.renderer,
      antialias: config.antialias,
    });
    logger.info(`Moteur initialisé (backend : ${backend}).`);

    const game = new Game({ canvas: target, config, engine, backend, registry });
    game.attach();
    await game.scenes.load(config.startScene);

    return game;
  }

  get currentSceneId(): SceneId | null {
    return this.scenes.currentId;
  }

  /** Démarre la boucle de rendu. Idempotent. */
  start(): void {
    if (this.running || this.disposed) return;
    this.running = true;

    this.engine.runRenderLoop(this.renderFrame);
  }

  /** Suspend la boucle de rendu sans rien détruire. */
  stop(): void {
    if (!this.running) return;
    this.running = false;
    this.engine.stopRenderLoop(this.renderFrame);
  }

  async goTo(id: SceneId): Promise<void> {
    try {
      await this.scenes.load(id);
    } catch (error) {
      logger.error(`Échec du chargement de la scène « ${id} » : ${describeError(error)}`);
    }
  }

  /** Détruit tout : scènes, entrées, observateurs, moteur. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    this.stop();
    this.resizeObserver.disconnect();
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.scenes.dispose();
    this.gauge.dispose();
    this.chopButton.dispose();
    this.joystick.dispose();
    this.harvest.dispose();
    this.input.dispose();
    this.engine.dispose();

    logger.info("Jeu détruit.");
  }

  private attach(): void {
    this.input.attach(window);
    this.resizeObserver.observe(this.canvas);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    this.applyHardwareScaling();
  }

  private readonly renderFrame = (): void => {
    const scene = this.scenes.current;
    if (!scene) return;

    // getDeltaTime() est en millisecondes ; on plafonne pour absorber les
    // longues pauses (onglet en arrière-plan, breakpoint) sans faire diverger
    // la simulation.
    const deltaSeconds = Math.min(this.engine.getDeltaTime() / 1000, this.config.maxDeltaSeconds);

    scene.update?.(deltaSeconds);
    scene.scene.render();
  };

  /**
   * Aligne la résolution de rendu sur le DPR, plafonné par la configuration.
   * `setHardwareScalingLevel(1)` = 1 pixel de rendu par pixel CSS ; 0.5 = deux
   * fois plus de pixels (écran Retina).
   */
  private applyHardwareScaling(): void {
    const ratio = Math.min(window.devicePixelRatio || 1, this.config.maxPixelRatio);
    this.engine.setHardwareScalingLevel(1 / ratio);
  }

  private readonly handleVisibilityChange = (): void => {
    // Onglet masqué : le navigateur throttle déjà requestAnimationFrame, mais
    // on relâche les touches pour ne pas revenir avec une action bloquée.
    if (document.hidden) this.input.reset();
  };
}
